import { useCallback, useContext } from 'react';
import { isEmpty } from 'lodash';
import { LayoutContextValues } from 'common/types/store';
import { LayoutContext } from 'context';
import { useDateQueryParam } from 'hooks';
import { checkEmptyObject, extractDate, getObjectValue } from 'utilities';
import { SetInitialMeasurementDateHook, UseSetInitialMeasurementDate } from './types';

const useSetInitialMeasurementDate: UseSetInitialMeasurementDate = params => {
  const { dateQueryString, measurementDates, openedMeasurementDate, setSelectedMeasurementDate } = params;

  const { commonMeasurementDate, setCommonMeasurementDate } = useContext(
    LayoutContext
  ) as unknown as LayoutContextValues;

  const { setDateQueryParam } = useDateQueryParam();

  const setInitialMeasurementDate: SetInitialMeasurementDateHook['setInitialMeasurementDate'] = useCallback(() => {
    const dateSlugParam = extractDate({
      dateSlug: typeof dateQueryString === 'string' ? dateQueryString : '',
    });

    const dateToMatch = commonMeasurementDate ?? dateSlugParam;
    const firstMeasurementDate = (measurementDates?.length && measurementDates[0]) || {};
    const sharedMeasurementDate = getObjectValue(
      measurementDates?.find(measurementDate => measurementDate.date === dateToMatch)
    );

    const defaultMD
      = checkEmptyObject(sharedMeasurementDate)
      ?? checkEmptyObject(openedMeasurementDate)
      ?? getObjectValue(firstMeasurementDate);

    if (!isEmpty(defaultMD)) {
      const { date, slug } = defaultMD;

      if (date) {
        setCommonMeasurementDate?.(date);
      }
      // For "Most Recent", slug is empty - don't set date query param
      // For other dates, use the date field (YYYY-MM-DD format) for the query param
      if (slug && date) {
        setDateQueryParam({ date });
      } else {
        // "Most Recent" selected - remove date query param
        setDateQueryParam({ date: '' });
      }

      setSelectedMeasurementDate(defaultMD);
    }
  }, [
    commonMeasurementDate,
    dateQueryString,
    measurementDates,
    openedMeasurementDate,
    setCommonMeasurementDate,
    setSelectedMeasurementDate,
    setDateQueryParam,
  ]);

  return {
    setInitialMeasurementDate,
  };
};

export default useSetInitialMeasurementDate;
